/**
 * Pure grouping of diff changes for the change browser (WHA-3695 UI follow-up).
 *
 * Groups the flat `changes[]` from `get_ifc_model_diff` into an ordered list of
 * collapsible groups, by one of three modes:
 *   - "class"      — by IFC class (IfcWall, IfcDoor…), labelled via `classLabel`
 *   - "discipline" — by AR/EK/VN/EL… via `disciplineKeyForClass` (OTHER catch-all)
 *   - "storey"     — by `storey_guid`, named/ordered from the diff's `storeys`
 *
 * Kept i18n-light and store-free so both hosts (the 3D rail and the standalone
 * overview modal) share it and it's trivially unit-testable.
 */

import type { IfcElementChange, IfcStoreyRef } from "@/stores/ifcModel/repository";
import {
  classLabel,
  disciplineColor,
  disciplineKeyForClass,
  disciplineLabel,
  DISCIPLINE_ORDER,
  OTHER,
} from "../disciplineMap";

export type GroupByMode = "class" | "storey" | "discipline";

interface DiffGroupCounts {
  added: number;
  removed: number;
  modified: number;
}

export interface DiffGroup {
  /** Stable group identity (ifc_class / discipline key / storey_guid / ""). */
  key: string;
  label: string;
  /** Discipline swatch colour ("#RRGGBB"); null for class/storey groups (the
   * change-type colours carry the meaning there). */
  color: string | null;
  counts: DiffGroupCounts;
  total: number;
  /** Every change in the group (already filtered by the caller's visibility). */
  elements: IfcElementChange[];
  /** Element GUIDs in the group — drives the 3D "highlight this group" action. */
  guids: string[];
}

interface GroupChangesOptions {
  language: "en" | "et";
  /** Storey lookup from the diff payload; required for meaningful storey mode. */
  storeys?: IfcStoreyRef[];
  /** Localized label for elements with no storey (storey mode only). */
  unassignedLabel: string;
}

const UNASSIGNED = "\u0000unassigned";

/** Bucket `changes` by a key function into an insertion-ordered map. */
function bucketBy(
  changes: IfcElementChange[],
  keyOf: (c: IfcElementChange) => string
): Map<string, IfcElementChange[]> {
  const buckets = new Map<string, IfcElementChange[]>();
  for (const c of changes) {
    const key = keyOf(c);
    let arr = buckets.get(key);
    if (!arr) {
      arr = [];
      buckets.set(key, arr);
    }
    arr.push(c);
  }
  return buckets;
}

function tally(elements: IfcElementChange[]): DiffGroupCounts {
  const counts: DiffGroupCounts = { added: 0, removed: 0, modified: 0 };
  for (const e of elements) counts[e.change_type] += 1;
  return counts;
}

function toGroup(
  key: string,
  label: string,
  color: string | null,
  elements: IfcElementChange[]
): DiffGroup {
  return {
    key,
    label,
    color,
    counts: tally(elements),
    total: elements.length,
    elements,
    guids: elements.map((e) => e.guid),
  };
}

/** Group `changes` by `mode`. The input is assumed already filtered to the
 * visible change types; grouping only buckets + orders. */
export function groupChanges(
  changes: IfcElementChange[],
  mode: GroupByMode,
  opts: GroupChangesOptions
): DiffGroup[] {
  if (mode === "storey") {
    const meta = new Map((opts.storeys ?? []).map((s) => [s.guid, s]));
    const buckets = bucketBy(changes, (c) => c.storey_guid ?? UNASSIGNED);
    const groups = [...buckets.entries()].map(([key, elements]) => {
      const label = key === UNASSIGNED ? opts.unassignedLabel : (meta.get(key)?.name ?? key);
      return toGroup(key, label, null, elements);
    });
    // Highest storey first; unassigned last.
    const elevationOf = (key: string): number =>
      key === UNASSIGNED ? Number.NEGATIVE_INFINITY : (meta.get(key)?.elevation ?? 0);
    return groups.sort((a, b) => elevationOf(b.key) - elevationOf(a.key));
  }

  if (mode === "discipline") {
    const buckets = bucketBy(changes, (c) => disciplineKeyForClass(c.ifc_class) ?? OTHER);
    const groups = [...buckets.entries()].map(([key, elements]) =>
      toGroup(key, disciplineLabel(key, opts.language), disciplineColor(key), elements)
    );
    const order = (key: string): number => {
      const i = DISCIPLINE_ORDER.indexOf(key);
      return i === -1 ? DISCIPLINE_ORDER.length : i;
    };
    return groups.sort((a, b) => order(a.key) - order(b.key) || b.total - a.total);
  }

  // mode === "class"
  const buckets = bucketBy(changes, (c) => c.ifc_class);
  const groups = [...buckets.entries()].map(([key, elements]) =>
    toGroup(key, classLabel(key), null, elements)
  );
  // Most-changed class first, then alphabetical.
  return groups.sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
}
